"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { Menu, X, ShoppingCart } from "lucide-react";
import { usePathname } from "next/navigation";
import { useAppDispatch, useAppSelector } from "@/_hooks/hooks";
import {
  getAllActiveBanner,
  getAllActiveSettings,
} from "@/services/home.services";
import { getAllActiveTours } from "@/services/package.services";
import { getAllActiveHotels } from "@/services/hotel.services";
import {
  getAllActiveSOUPackages,
  getAllActiveTickets,
} from "@/services/ticket.services";
import Preloader from "../home/Preloader";

export default function Header() {
  const dispatch = useAppDispatch();
  const pathname = usePathname();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { cartItems, hotelCart } = useAppSelector((state) => state.cart);
  const bannerLoading = useAppSelector((state) => state.home.bannerLoading);
  const totalItems = (cartItems?.length || 0) + (hotelCart?.length || 0);

  useEffect(() => {
    dispatch(getAllActiveBanner());
    dispatch(getAllActiveSettings());
    dispatch(getAllActiveTours());
    dispatch(getAllActiveHotels());
    dispatch(getAllActiveTickets());
    dispatch(getAllActiveSOUPackages());
  }, []);

  const menuItems = [
    { name: "Home", href: "/" },
    { name: "Tickets", href: "/sou-tickets" },
    { name: "SOU Packages", href: "/sou-packages" },
    { name: "Hotels", href: "/sou-hotels" },
    // { name: "Gujarat Packages", href: "/gujarat-packages" },
    // { name: "Blogs", href: "/blogs" },
    { name: "About Us", href: "/about" },
    { name: "Contact", href: "/contact" },
  ];

  return (
    <>
      {bannerLoading && <Preloader />}
      <header className="sticky top-0 z-[2000] bg-white/95 backdrop-blur-md shadow-sm border-b border-gray-100">
        {/* Tricolor top accent */}
        <div className="h-1 flex">
          <div className="flex-1 bg-orange-500"></div>
          <div className="flex-1 bg-white"></div>
          <div className="flex-1 bg-green-600"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Logo */}
            <div className="flex items-center md:gap-9 gap-1 scale-[0.75] -translate-x-8 sm:scale-[1] sm:translate-x-0">
              <Link href="/" className="flex-shrink-0">
                <Image
                  src="/logo.jpg"
                  alt="Statue of Unity Tours"
                  width={140}
                  height={48}
                  className="h-12 w-auto"
                />
              </Link>
              <Link href="/" className="flex-shrink-0">
                <Image
                  src="/aalpine logo header.png"
                  alt="Statue of Unity Tours"
                  width={140}
                  height={48}
                  className="h-12 w-auto"
                />
              </Link>
            </div>

            <div className="flex items-center gap-3 xl:gap-5">
              {/* Desktop Navigation */}
              <nav className="hidden lg:flex items-center space-x-6 xl:space-x-8">
                {menuItems.map((item) => {
                  const isActive =
                    pathname === item.href ||
                    (item.href !== "/" && pathname.startsWith(item.href));
                  return (
                    <Link
                      key={item.name}
                      href={item.href}
                      className={`${
                        isActive ? "text-orange-600" : "text-gray-700"
                      } font-medium hover:text-orange-600 transition-colors duration-200 text-[13px] xl:text-sm`}
                    >
                      {item.name}
                    </Link>
                  );
                })}
              </nav>

              {/* Cart Icon */}
              <Link
                href="/cart"
                className="relative p-2.5 rounded-xl hover:bg-orange-50 group transition-all duration-300"
              >
                <ShoppingCart className="w-5 h-5 xl:w-6 xl:h-6 text-gray-700 group-hover:text-orange-600 transition-colors" />
                {totalItems > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 bg-orange-600 text-white text-[10px] font-bold rounded-full flex items-center justify-center border-2 border-white shadow-md animate-in zoom-in duration-300">
                    {totalItems}
                  </span>
                )}
              </Link>

              {/* Mobile menu button */}
              <button
                className="lg:hidden p-2 rounded-lg hover:bg-orange-50 transition-colors duration-200 appearance-none"
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                aria-label="Toggle Navigation"
              >
                {isMenuOpen ? (
                  <X className="w-6 h-6 text-gray-700" />
                ) : (
                  <Menu className="w-6 h-6 text-gray-700" />
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 bg-white shadow-xl absolute w-full">
            <div className="max-w-7xl mx-auto px-4 py-4 space-y-2">
              {menuItems.map((item) => {
                const isActive =
                  pathname === item.href ||
                  (item.href !== "/" && pathname.startsWith(item.href));
                return (
                  <Link
                    key={item.name}
                    href={item.href}
                    className={`block py-3 px-4 ${
                      isActive
                        ? "text-orange-600 bg-orange-50"
                        : "text-gray-700 hover:text-orange-600 hover:bg-orange-50"
                    } font-medium rounded-lg transition-colors duration-200`}
                    onClick={() => setIsMenuOpen(false)}
                  >
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>
        )}
      </header>
    </>
  );
}
